import React, { useContext } from 'react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useAISettings } from '../../../hooks/useAISettings';
import { ExternalLink, AlertTriangle } from 'lucide-react';
import { AISettingsContext } from '../../../pages/settings/AISettings';

export function PromptSettings() {
  const { chatbotPublicId } = useChatbot();
  const { prompts, updatePrompts, loading } = useAISettings();
  const { isOverLimit } = useContext(AISettingsContext);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!chatbotPublicId || isOverLimit) return;
    
    const formData = new FormData(e.currentTarget);
    const generalPrompt = formData.get('generalPrompt') as string;
    const attitudePrompt = formData.get('attitudePrompt') as string;
    updatePrompts({ generalPrompt, attitudePrompt }, chatbotPublicId);
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Prompts configurations</h3>
        <a 
          href="https://prompt.botmaker-agency.ai" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors"
        >
          Access to our prompt generator
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            General Prompt (Role and Responsibilities)
          </label>
          <textarea
            name="generalPrompt"
            defaultValue={prompts.generalPrompt}
            rows={4}
            className={`w-full bg-white/5 border ${isOverLimit ? 'border-red-500/50' : 'border-white/10'} rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            placeholder="Describe the role and responsibilities of your assistant..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Attitude Prompt (Tone and Behavior)
          </label>
          <textarea
            name="attitudePrompt"
            defaultValue={prompts.attitudePrompt}
            rows={4}
            className={`w-full bg-white/5 border ${isOverLimit ? 'border-red-500/50' : 'border-white/10'} rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            placeholder="Describe the desired tone and behavior..."
          />
        </div>

        <div className="flex items-center justify-between">
          <button
            type="submit"
            disabled={loading || isOverLimit}
            className="bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-lg hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save prompts'}
          </button>

          {isOverLimit && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>Cannot save - Character limit exceeded</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}